/**
 * UnsavedChangesGuard — a dirty form asks before it is navigated away from.
 *
 * One shared component, adopted per form: pass the form's `formState.isDirty`
 * (minus the states where leaving is fine — see `when` below) and the name of
 * the thing being edited. Rendered anywhere inside the form's page.
 *
 * Uses react-router's `useBlocker`, which REQUIRES a data router — that is
 * why `main.tsx` mounts `createBrowserRouter` rather than `<BrowserRouter>`
 * (and why specs for adopting pages render through
 * `test/helpers/router-wrapper.tsx` instead of `MemoryRouter`; under a plain
 * router this hook throws at render). One blocker per page: react-router
 * supports a single active blocker, which a one-form-per-page app satisfies
 * naturally — do not mount two of these on one route.
 *
 * The confirm step is the shared `AlertDialog`, same as every destructive
 * confirm in this app: discarding edits destroys them, and a hand-rolled
 * confirm would get focus trapping and Escape wrong on exactly the control
 * that loses work. Cancel ("Keep editing") is the focused default, so Enter
 * without reading keeps the work.
 *
 * A form whose SUCCESS ends in a navigation must go through
 * `useLeaveAfterSave` below rather than calling `navigate()` from
 * `onSuccess`, and include its `leaving` in `when`:
 *
 *   const { leaving, leave } = useLeaveAfterSave()
 *   …
 *   onSuccess: (created) => leave(`/admin/students/${created.userId}/edit`)
 *   …
 *   <UnsavedChangesGuard
 *     when={formState.isDirty && !leaving}
 *     name="the new student"
 *   />
 *
 * Why: `useBlocker` evaluates the predicate committed by the LAST render,
 * and `onSuccess` runs in a microtask — with a fast (or test-stubbed)
 * mutation, nothing has re-rendered since the operator clicked, so a
 * direct `navigate()` still sees "dirty, not saving" and the guard blocks
 * the form's own success redirect. `leave()` makes the ordering
 * structural instead of temporal: it sets state, React commits it (the
 * guard's blocker updates in that commit — child effects run first), and
 * only then does the effect navigate.
 *
 * (Do NOT reach for `!save.isSuccess` instead: on a stay-and-add-another
 * form the mutation stays `isSuccess` forever after the first create,
 * which would silently disarm the guard for everything typed afterwards.
 * Edit forms clear `isDirty` via `reset(freshRow)` on success; forms that
 * leave use `leave()`.)
 *
 * Same-page navigations (search-param changes) are deliberately not blocked:
 * the form is still on screen, so nothing is lost.
 */
import { useRefreshBlocker } from '@speaking-coach/ui'
import { useEffect, useRef, useState } from 'react'
import { useBlocker, useNavigate } from 'react-router-dom'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@speaking-coach/ui'

const EXTERNAL_LEAVE_REQUEST = 'coach-admin:external-leave-request'
let authorizedDocumentExit = false
let authorizationResetTimer: number | null = null

function clearAuthorizedDocumentExit(): void {
  authorizedDocumentExit = false
  if (authorizationResetTimer !== null) {
    window.clearTimeout(authorizationResetTimer)
    authorizationResetTimer = null
  }
}

function authorizeNextDocumentExit(): void {
  clearAuthorizedDocumentExit()
  authorizedDocumentExit = true
}

function consumeAuthorizedDocumentExit(): boolean {
  if (!authorizedDocumentExit) return false
  clearAuthorizedDocumentExit()
  return true
}

/** Clear a grant on the next task if an attempted external navigation stayed put. */
export function resetDocumentExitAuthorizationAfterAttempt(): void {
  if (!authorizedDocumentExit || authorizationResetTimer !== null) return
  authorizationResetTimer = window.setTimeout(clearAuthorizedDocumentExit, 0)
}

interface ExternalLeaveRequestDetail {
  respond: (discard: boolean) => void
}

/**
 * Ask the mounted dirty-form guard before an exit that React Router cannot
 * observe, such as signing out to the customer-facing login origin.
 */
export function requestDiscardUnsavedChanges(): Promise<boolean> {
  return new Promise((resolve) => {
    let answered = false
    const respond = (discard: boolean): void => {
      if (answered) return
      answered = true
      resolve(discard)
    }
    const event = new CustomEvent<ExternalLeaveRequestDetail>(
      EXTERNAL_LEAVE_REQUEST,
      { cancelable: true, detail: { respond } },
    )
    window.dispatchEvent(event)
    if (!event.defaultPrevented) respond(true)
  })
}

/**
 * The navigation half of a successful save — see the wiring note above.
 *
 * `leave(to)` records the destination; the navigation itself happens in an
 * effect, one commit later, so every `useBlocker` predicate on the page has
 * been updated with `leaving === true` before the router is asked to move.
 */
export function useLeaveAfterSave(): {
  /** True from `leave()` until the page unmounts. Include as `!leaving` in `when`. */
  leaving: boolean
  /** Navigate to `to` after the next commit. */
  leave: (to: string) => void
} {
  const navigate = useNavigate()
  const [target, setTarget] = useState<string | null>(null)
  useEffect(() => {
    if (target !== null) navigate(target)
  }, [target, navigate])
  return { leaving: target !== null, leave: setTarget }
}

export interface UnsavedChangesGuardProps {
  /** Block navigation while true — the form's dirty-and-not-saving state. */
  when: boolean
  /**
   * What the unsaved changes belong to, named in the dialog — an operator
   * with several tabs open should not have to trust which page this is.
   * E.g. "this student", "the new client".
   */
  name: string
}

export function UnsavedChangesGuard({
  when,
  name,
}: UnsavedChangesGuardProps): JSX.Element | null {
  useRefreshBlocker(when)
  const decisionMade = useRef(false)
  const [externalResponse, setExternalResponse] = useState<
    ((discard: boolean) => void) | null
  >(null)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  )

  // If the reason to block disappears while the dialog is up (a background
  // refetch reset the form, say), let the navigation through rather than
  // asking about changes that no longer exist.
  useEffect(() => {
    if (blocker.state === 'blocked' && !when) blocker.proceed()
  }, [blocker, when])

  useEffect(() => {
    const request = (rawEvent: Event): void => {
      if (!when) return
      clearAuthorizedDocumentExit()
      decisionMade.current = false
      const event = rawEvent as CustomEvent<ExternalLeaveRequestDetail>
      event.preventDefault()
      setExternalResponse(() => event.detail.respond)
    }
    window.addEventListener(EXTERNAL_LEAVE_REQUEST, request)
    return () => window.removeEventListener(EXTERNAL_LEAVE_REQUEST, request)
  }, [when])

  useEffect(() => {
    if (!when) return
    const warnBeforeDocumentExit = (event: BeforeUnloadEvent): void => {
      if (consumeAuthorizedDocumentExit()) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeDocumentExit)
    return () => window.removeEventListener('beforeunload', warnBeforeDocumentExit)
  }, [when])

  useEffect(() => {
    if (externalResponse !== null && !when) {
      authorizeNextDocumentExit()
      externalResponse(true)
      setExternalResponse(null)
    }
  }, [externalResponse, when])

  if (blocker.state !== 'blocked' && externalResponse === null) return null

  const keepEditing = (): void => {
    if (externalResponse !== null) {
      clearAuthorizedDocumentExit()
      externalResponse(false)
      setExternalResponse(null)
      return
    }
    if (blocker.state === 'blocked') blocker.reset()
  }

  const discardChanges = (): void => {
    decisionMade.current = true
    if (externalResponse !== null) {
      authorizeNextDocumentExit()
      externalResponse(true)
      setExternalResponse(null)
      return
    }
    if (blocker.state === 'blocked') blocker.proceed()
  }

  return (
    // Escape means "Keep editing": Radix reports it through
    // `onOpenChange(false)`, and canceling is the right reading of Escape —
    // the destructive answer should never be the one a reflex key gives.
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open && decisionMade.current) {
          decisionMade.current = false
          return
        }
        if (!open) keepEditing()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
          <AlertDialogDescription>
            Your changes to {name} have not been saved. Leaving this page
            discards them.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={discardChanges}>
            Discard changes
          </AlertDialogAction>
          <AlertDialogCancel onClick={keepEditing}>
            Keep editing
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
